import React from 'react'
import { Navigate } from 'react-router-dom'
import { useI18n } from '../../i18n'
import { featureFlags } from '../../config/featureFlags'
import { GovernanceListingSection } from '../../components/governance/GovernanceListingSection'
import listStyles from '@/styles/pages/governance/GovernanceListing.module.scss'
import OperationalEventTimeline from '../../components/compliance-runtime/OperationalEventTimeline'
import { Button } from '../../components/ui/Button'
import {
  fetchRuntimeEvents,
  fetchRuntimeDegraded,
  fetchRuntimeRecovering,
  recomputeComplianceRuntime,
} from '../../services/complianceRuntimeApi'

type Tab = 'all' | 'degraded' | 'recovering'

export default function ComplianceCenterOperationalEventsWorkspace() {
  const { t } = useI18n()
  const [tab, setTab] = React.useState<Tab>('all')
  const [loading, setLoading] = React.useState(true)
  const [busy, setBusy] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [events, setEvents] = React.useState<Array<{ id: string; at: string; eventType: string; severity: string; summary: string }>>([])

  const load = React.useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      if (tab === 'degraded') {
        const d = await fetchRuntimeDegraded()
        setEvents(
          (d.degraded || []).map((e) => ({
            id: e.id,
            at: e.createdAt,
            eventType: e.eventType,
            severity: e.severity,
            summary: e.explanations?.summary || e.eventType,
          })),
        )
      } else if (tab === 'recovering') {
        const r = await fetchRuntimeRecovering()
        setEvents(
          (r.recovering || []).map((e) => ({
            id: e.id,
            at: e.createdAt,
            eventType: e.eventType,
            severity: e.severity,
            summary: e.explanations?.summary || e.eventType,
          })),
        )
      } else {
        const all = await fetchRuntimeEvents()
        setEvents(
          (all.events || []).map((e) => ({
            id: e.id,
            at: e.createdAt,
            eventType: e.eventType,
            severity: e.severity,
            summary: e.explanations?.summary || e.eventType,
          })),
        )
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load events')
    } finally {
      setLoading(false)
    }
  }, [tab])

  React.useEffect(() => {
    void load()
  }, [load])

  const handleRecompute = async () => {
    setBusy(true)
    try {
      await recomputeComplianceRuntime()
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Recompute failed')
    } finally {
      setBusy(false)
    }
  }

  if (!featureFlags.COMPLIANCE_RUNTIME_V1) {
    return <Navigate to="/compliance-center/overview" replace />
  }

  const tabs: { id: Tab; label: string }[] = [
    { id: 'all', label: t('complianceRuntime.allEvents', 'All events') },
    { id: 'degraded', label: t('complianceRuntime.operationalDegradation', 'Operational degradation') },
    { id: 'recovering', label: t('complianceRuntime.operationalRecovery', 'Operational recovery') },
  ]

  return (
    <div className={listStyles.pageStack}>
      <GovernanceListingSection
        title={t('complianceCenterUi.nav.operationalEvents', 'Operational events')}
        description={t('complianceRuntime.workspaceIntro', 'Event-driven compliance runtime — deterministic propagation from existing telemetry.')}
        toolbar={
          <Button type="button" size="small" color="brand" disabled={busy || loading} onClick={handleRecompute}>
            {t('complianceRuntime.recompute', 'Reconcile & emit events')}
          </Button>
        }
      >
        <div className={listStyles.segmentedTabs} role="tablist">
          {tabs.map((item) => (
            <button
              key={item.id}
              type="button"
              role="tab"
              aria-selected={tab === item.id}
              className={`${listStyles.segmentedTab} ${tab === item.id ? listStyles.segmentedTabActive : ''}`}
              onClick={() => setTab(item.id)}
            >
              {item.label}
            </button>
          ))}
        </div>
        {loading ? <p className="text-sm text-slate-500">{t('common.loading', 'Loading…')}</p> : null}
        {error ? <p className="text-sm text-red-600">{error}</p> : null}
      </GovernanceListingSection>

      {!loading ? (
        <GovernanceListingSection title={t('complianceRuntime.runtimeOrchestration', 'Runtime orchestration')}>
          <OperationalEventTimeline items={events} />
        </GovernanceListingSection>
      ) : null}
    </div>
  )
}
